import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Edit2, Phone, MapPin, Briefcase, IdCard, GraduationCap, Calendar, Clock, UserCheck, X } from 'lucide-react';
import api from '../api/axios';
import StatusBadge from '../components/registrations/StatusBadge';
import { formatDate } from '../utils/formatDate';
import { useAuth } from '../context/AuthContext';

const STATUS_BUTTON_STYLES = {
  SUBMITTED: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
  REJECTED: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
};

export default function RegistrationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingStatus, setEditingStatus] = useState(false);

  useEffect(() => {
    api.get(`/registrations/${id}`)
      .then(res => setReg(res.data))
      .catch(() => navigate('/registrations'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const updateStatus = async (status) => {
    try {
      const res = await api.put(`/registrations/${id}`, { status });
      setReg(res.data);
      setEditingStatus(false);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <div className="w-12 h-12 rounded-full border-4 border-ysrcp-100 border-t-ysrcp-500 animate-spin" />
      <p className="text-sm text-gray-400">Loading details...</p>
    </div>
  );
  if (!reg) return null;

  const Section = ({ title, icon: Icon, children }) => (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <div className="w-8 h-8 rounded-lg bg-ysrcp-50 flex items-center justify-center"><Icon className="w-4 h-4 text-ysrcp-500" /></div>}
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">{title}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">{children}</div>
    </div>
  );

  const Field = ({ label, value }) => (
    <div className="group">
      <dt className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-gray-900 mt-1 font-medium">{value || <span className="text-gray-300">—</span>}</dd>
    </div>
  );

  const canEdit = authUser?.role === 'SUPER_ADMIN' || authUser?.role === 'AC_ADMIN';

  return (
    <div className="space-y-5">
      {/* Back button */}
      <button onClick={() => navigate('/registrations')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-ysrcp-600 transition-colors font-medium">
        <ArrowLeft className="w-4 h-4" /> Back to Registrations
      </button>

      {/* Profile Header Card */}
      <div className="bg-white rounded-xl shadow-panel border border-gray-100 overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-ysrcp-600 via-ysrcp-500 to-ysrcp-400" />
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            {/* Photo */}
            <div className="relative">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-ysrcp-100 to-ysrcp-200 flex-shrink-0 flex items-center justify-center overflow-hidden ring-4 ring-white shadow-lg">
                {reg.photo_url ? (
                  <img src={reg.photo_url} alt={reg.full_name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-ysrcp-400" />
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-gray-900">{reg.full_name}</h2>
              <p className="text-sm text-gray-400 mt-1 font-mono">{reg.user_id}</p>

              {/* Quick info chips */}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <StatusBadge status={reg.status} />
                {reg.committee_type && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-ysrcp-50 text-ysrcp-700">
                    <Briefcase className="w-3 h-3" /> {reg.committee_type}
                  </span>
                )}
                {reg.position && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {reg.position}
                  </span>
                )}
                {reg.assembly_name && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-ysrcp-green-50 text-ysrcp-green-700">
                    <MapPin className="w-3 h-3" /> {reg.assembly_name}
                  </span>
                )}
              </div>

              {/* Status edit */}
              {canEdit && (
                <div className="mt-3">
                  {!editingStatus ? (
                    <button onClick={() => setEditingStatus(true)} className="inline-flex items-center gap-1.5 text-xs text-ysrcp-600 hover:text-ysrcp-700 font-medium transition-colors">
                      <Edit2 className="w-3 h-3" /> Change Status
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      {['SUBMITTED', 'APPROVED', 'REJECTED', 'PENDING'].map(s => (
                        <button
                          key={s}
                          onClick={() => updateStatus(s)}
                          className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-all ${
                            reg.status === s
                              ? 'ring-2 ring-ysrcp-200 ' + STATUS_BUTTON_STYLES[s]
                              : STATUS_BUTTON_STYLES[s]
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                      <button onClick={() => setEditingStatus(false)} className="text-xs text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Personal Info */}
        <div className="bg-white rounded-xl shadow-panel border border-gray-100 p-5 sm:p-6">
          <Section title="Personal Information" icon={UserCheck}>
            <Field label="Full Name" value={reg.full_name} />
            <Field label="Father/Husband Name" value={reg.father_husband_name} />
            <Field label="Gender" value={reg.gender} />
            <Field label="Age" value={reg.age} />
            <Field label="Qualification" value={reg.qualification} />
            <Field label="Caste Category" value={reg.caste_category} />
            <Field label="Caste" value={reg.caste} />
            <Field label="Profession" value={reg.profession} />
          </Section>
        </div>

        {/* Committee Info */}
        <div className="bg-white rounded-xl shadow-panel border border-gray-100 p-5 sm:p-6">
          <Section title="Committee Information" icon={Briefcase}>
            <Field label="Committee Type" value={reg.committee_type} />
            <Field label="Position" value={reg.position} />
            <Field label="Affiliated Wing" value={reg.affiliated_wing} />
            <Field label="Level" value={reg.level} />
          </Section>
        </div>

        {/* Contact & Identity */}
        <div className="bg-white rounded-xl shadow-panel border border-gray-100 p-5 sm:p-6">
          <Section title="Contact & Identity" icon={IdCard}>
            <Field label="Voter ID" value={reg.voter_id} />
            <Field label="Phone Number" value={reg.phone_number} />
          </Section>
        </div>

        {/* Location */}
        <div className="bg-white rounded-xl shadow-panel border border-gray-100 p-5 sm:p-6">
          <Section title="Location" icon={MapPin}>
            <Field label="District" value={reg.district} />
            <Field label="Assembly Constituency" value={reg.assembly_constituency || reg.assembly_name} />
            <Field label="Parliamentary Constituency" value={reg.pc} />
            <Field label="Mandal" value={reg.mandal} />
            <Field label="Panchayat" value={reg.panchayat} />
          </Section>
        </div>
      </div>

      {/* Timeline - full width */}
      <div className="bg-white rounded-xl shadow-panel border border-gray-100 p-5 sm:p-6">
        <Section title="Timeline" icon={Clock}>
          <Field label="Created Date" value={formatDate(reg.created_date)} />
          <Field label="Created By" value={reg.created_by_name} />
          <Field label="Last Modified" value={formatDate(reg.last_modified_date)} />
          <Field label="Imported At" value={formatDate(reg.imported_at)} />
        </Section>
      </div>
    </div>
  );
}
